import config from 'Cloud/config';
import React, { type ReactElement, memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { BaseInputDialog } from 'reactApp/components/BaseConfirmDialog/BaseInputDialog';
import { VK_UI_DARK_THEME } from 'reactApp/constants';
import { NEW_FOLDER_DEFAULT_NAME } from 'reactApp/constants/magicIdentificators';
import { getCurrentSharedTab } from 'reactApp/modules/shared/shared.selectors';
import { SharedTab } from 'reactApp/modules/shared/shared.type';
import { noop } from 'reactApp/utils/helpers';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import type { IProps } from './NewFolderModal.types';

const MAX_NAME_LENGTH = config.get('ITEM_NAME_MAX_LENGTH');

export const NewFolderModal = memo(
    ({
        showShareButton,
        showCreateButton,
        createFolder,
        onClose,
        title = 'Создание папки',
        placeholder,
        folderName = NEW_FOLDER_DEFAULT_NAME,
        isDark = false,
    }: IProps): ReactElement => {
        const currentTab = useSelector(getCurrentSharedTab) || SharedTab.links;

        const renderHeader = useMemo(() => {
            return typeof title === 'function' ? title : () => title;
        }, [title]);

        const buttons = useMemo(() => {
            if (showCreateButton && showShareButton) {
                return [EButtonType.accept, EButtonType.action];
            }

            return [EButtonType.accept];
        }, [showCreateButton, showShareButton]);

        const onSuccessCallback = useCallback(
            (folderName) => {
                sendPaymentGa({
                    eventCategory: ECategoryGa.created,
                    action: 'created-vigbo-gallery',
                });
                return createFolder(folderName, !showCreateButton);
            },
            [createFolder, showCreateButton]
        );

        const onSuccessAndShareCallback = useCallback(
            (folderName) => {
                return createFolder(folderName, true);
            },
            [createFolder]
        );

        const bttnText = currentTab === SharedTab.indexed ? 'Создать и опубликовать' : 'Создать и поделиться';

        return (
            <BaseInputDialog
                className={isDark ? VK_UI_DARK_THEME : ''}
                dataQAId="new-folder-modal"
                renderHeader={renderHeader}
                successButtonText={showCreateButton ? 'Создать' : bttnText}
                actionButtonText={bttnText}
                buttons={buttons}
                onAction={onSuccessAndShareCallback}
                onSuccess={onSuccessCallback}
                selectionRange={folderName.length}
                maxLength={MAX_NAME_LENGTH}
                value={folderName}
                onClose={onClose}
                renderContent={noop}
                placeholder={placeholder}
            />
        );
    }
);

NewFolderModal.displayName = 'NewFolderModal';
